/**
 * EW Tools (POC)
 *
 * Preview of a future staff-facing portal for BBH's browser-automation tools
 * (GRIP, EECA, Tenancy Services). Client-side only — no real automation is
 * wired up. See project-brain/pages/ew-tools.md for the spec, and
 * https://github.com/BackedbyHumans/EW-brain/issues/5 for the real Outcome.
 */

import React, { useState } from 'react';
import Card from '@components/ui/Card';
import Badge from '@components/ui/Badge';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import './EWTools.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type ToolId = 'grip' | 'eeca' | 'tenancy';

interface Tool {
  id: ToolId;
  name: string;
  description: string;
  outcomeUrl: string;
  status: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const TOOLS: Tool[] = [
  {
    id: 'grip',
    name: 'GRIP Parcel Lookup',
    description: 'Given an address, retrieve property/parcel information from GRIP (app.grip.co.nz) — appellation, land district, area, titles, owners.',
    outcomeUrl: 'https://github.com/BackedbyHumans/EW-brain/issues/4',
    status: 'Feasibility tested',
  },
  {
    id: 'eeca',
    name: 'EECA Warmer Kiwi Homes Eligibility',
    description: 'Runs the EECA Warmer Kiwi Homes eligibility checker for a given set of answers and an address.',
    outcomeUrl: 'https://github.com/BackedbyHumans/EW-brain/issues/2',
    status: 'Feasibility tested',
  },
  {
    id: 'tenancy',
    name: 'Tenancy Services Heating Assessment',
    description: 'Runs the Tenancy Services heating assessment tool’s progressive question flow.',
    outcomeUrl: 'https://github.com/BackedbyHumans/EW-brain/issues/3',
    status: 'Feasibility tested (partial)',
  },
];

const GRIP_DEMO_ADDRESS = '7 dunsinane place, marton';

const GRIP_MOCK_RESULT: Array<[string, string]> = [
  ['Appellation', 'Lot 7 DP 45873'],
  ['Address', '7 Dunsinane Place, Marton'],
  ['Land District', 'Wellington'],
  ['Surveyed Area', '655m²'],
  ['Calculated Area', '654m²'],
  ['Parcel Intent', 'DCDB'],
  ['Parcel Id', '3757281'],
  ['Parcel Status', 'Current'],
  ['Titles', 'WN17B/1136'],
  ['Owners', 'Ayla Hellita Schreiber'],
];

// ─── Component ────────────────────────────────────────────────────────────────

const EWTools: React.FC = () => {
  const [selectedTool, setSelectedTool] = useState<ToolId>('grip');
  const [address, setAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<
    | { kind: 'grip-match'; address: string }
    | { kind: 'grip-no-match'; address: string }
    | { kind: 'not-wired'; toolName: string }
    | null
  >(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setResult(null);
    setSubmitting(true);

    // Simulated delay — the real automations take a few seconds (a real
    // headless browser run), so the demo mimics that instead of resolving instantly.
    setTimeout(() => {
      if (selectedTool === 'grip') {
        if (address.trim().toLowerCase() === GRIP_DEMO_ADDRESS) {
          setResult({ kind: 'grip-match', address: address.trim() });
        } else {
          setResult({ kind: 'grip-no-match', address: address.trim() });
        }
      } else {
        const tool = TOOLS.find((t) => t.id === selectedTool);
        setResult({ kind: 'not-wired', toolName: tool?.name ?? selectedTool });
      }
      setSubmitting(false);
    }, 900);
  };

  return (
    <div className="ew-tools-page">
      <div className="ew-tools-header">
        <h1 className="ew-tools-title">EW Tools</h1>
        <Badge variant="warning">POC — not live</Badge>
      </div>

      <div className="ew-tools-banner">
        This is a proof of concept for a future staff self-service portal (
        <a href="https://github.com/BackedbyHumans/EW-brain/issues/5" target="_blank" rel="noopener noreferrer">
          EW-brain Outcome #5
        </a>
        ). It is <strong>not connected to any live automation</strong> — results below are simulated for demonstration only.
      </div>

      {/* ── Tool list ──────────────────────────────────────────────────────── */}
      <section className="ew-tools-section">
        <h2 className="ew-tools-section-title">Available tools</h2>
        <div className="ew-tools-grid">
          {TOOLS.map((tool) => (
            <Card key={tool.id} className="ew-tools-card">
              <div className="ew-tools-card-header">
                <span className="ew-tools-card-name">{tool.name}</span>
                <Badge variant="success">{tool.status}</Badge>
              </div>
              <p className="ew-tools-card-desc">{tool.description}</p>
              <a
                href={tool.outcomeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ew-tools-card-link"
              >
                View Outcome &rarr;
              </a>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Demo form ──────────────────────────────────────────────────────── */}
      <section className="ew-tools-section">
        <h2 className="ew-tools-section-title">Try it (demo)</h2>
        <Card>
          <form className="ew-tools-form" onSubmit={handleSubmit}>
            <div className="ew-tools-form-row">
              <label className="input-label" htmlFor="ew-tools-select">Tool</label>
              <select
                id="ew-tools-select"
                className="ew-tools-select"
                value={selectedTool}
                onChange={(e) => {
                  setSelectedTool(e.target.value as ToolId);
                  setResult(null);
                }}
              >
                {TOOLS.map((tool) => (
                  <option key={tool.id} value={tool.id}>{tool.name}</option>
                ))}
              </select>
            </div>

            <Input
              label="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="e.g. 7 Dunsinane Place, Marton"
            />

            <div className="ew-tools-form-actions">
              <Button type="submit" variant="primary" disabled={submitting || !address.trim()}>
                {submitting ? 'Looking up…' : 'Look up'}
              </Button>
            </div>
          </form>

          {submitting && (
            <div className="ew-tools-result-loading">
              <LoadingSpinner size="sm" />
            </div>
          )}

          {!submitting && result && (
            <div className="ew-tools-result">
              <div className="ew-tools-result-header">
                <span>Result</span>
                <Badge variant="warning">Simulated result</Badge>
              </div>

              {result.kind === 'grip-match' && (
                <table className="ew-tools-result-table">
                  <tbody>
                    {GRIP_MOCK_RESULT.map(([field, value]) => (
                      <tr key={field}>
                        <th>{field}</th>
                        <td>{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {result.kind === 'grip-no-match' && (
                <p className="ew-tools-result-empty">
                  No demo data for &ldquo;{result.address}&rdquo;. This POC only simulates a result for the
                  tested address &mdash; try &ldquo;7 Dunsinane Place, Marton&rdquo;.
                </p>
              )}

              {result.kind === 'not-wired' && (
                <p className="ew-tools-result-empty">
                  {result.toolName} isn&rsquo;t wired into this demo yet &mdash; this placeholder just shows
                  where its result would appear.
                </p>
              )}
            </div>
          )}
        </Card>
      </section>
    </div>
  );
};

export default EWTools;
